// src/context/CartContext.js - Shopping Cart State Management
import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const stored = localStorage.getItem('tiwatinwa_cart');
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('tiwatinwa_cart', JSON.stringify(cart));
  }, [cart]);

  // Cart Actions
  const addToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(i => i.id === item.id);
      
      if (existingItem) {
        return prevCart.map(i =>
          i.id === item.id
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      
      return [...prevCart, { ...item, quantity: 1 }];
    });
    
    // Show success notification
    if (window.showCartNotification) {
      window.showCartNotification(`🛒 ${item.name} added to cart!`);
    }
  };

  const removeFromCart = (itemId) => {
    setCart((prevCart) => prevCart.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    setCart((prevCart) =>
      prevCart.map(item =>
        item.id === itemId
          ? { ...item, quantity: quantity <= 0 ? 1 : quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  // Cart Calculations
  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getDeliveryFee = () => {
    const total = getCartTotal();
    if (total === 0) return 0;
    if (total >= 5000) return 0; // Free delivery for orders over ₦5000
    return 500; // Standard delivery fee
  };

  const getFinalTotal = () => {
    return getCartTotal() + getDeliveryFee();
  };

  // Context Value
  const value = {
    // State
    cart,
    
    // Actions
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    
    // Calculations
    getCartTotal,
    getCartItemCount,
    getDeliveryFee,
    getFinalTotal,
    
    // Utilities
    isInCart: (itemId) => cart.some(item => item.id === itemId),
    getItemQuantity: (itemId) => {
      const item = cart.find(item => item.id === itemId);
      return item ? item.quantity : 0;
    }
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;